import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Package, Plus, ArrowRight } from 'lucide-react'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { ProductCard } from '@/consumer/components/product-card'
import { ClaimCard } from '@/consumer/components/claim-card'
import { Button } from '@/shared/components/ui/button'
import { useConsumerHoldings } from '@/consumer/hooks/use-holdings'
import {
  useConsumerClaims,
  type ConsumerClaimSummary,
} from '@/consumer/hooks/use-consumer-claims'
import { useSelfRegistrationEnabled } from '@/consumer/hooks/use-self-registration'

// Homepage shows an overview of each section, capped at this many cards; the
// rest live on the section's dedicated "See all" page (WPS-138).
const HOME_SECTION_LIMIT = 4

function SectionHeader({
  title,
  countLabel,
  seeAllTo,
  action,
}: {
  title: string
  countLabel: string
  seeAllTo?: () => void
  action?: React.ReactNode
}) {
  return (
    <div className="mb-4 flex items-end justify-between">
      <div>
        <h2 className="clara-heading mb-0.5">{title}</h2>
        <p className="text-[12px] text-[var(--ink-3)]">{countLabel}</p>
      </div>
      <div className="flex items-center gap-3">
        {seeAllTo && (
          <button
            type="button"
            onClick={seeAllTo}
            className="group flex cursor-pointer items-center gap-1 text-[12px] text-[var(--ink-3)] transition-colors hover:text-[var(--ink)]"
          >
            See all
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
        {action}
      </div>
    </div>
  )
}

export default function WalletPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: holdings, isPending: holdingsPending } = useConsumerHoldings()
  const { data: claims, isPending: claimsPending } = useConsumerClaims()
  // WPS-138 #1: hide the "Register Product" CTAs for tenants that do not offer
  // consumer self-registration (OC at go-live).
  const selfRegistrationEnabled = useSelfRegistrationEnabled()

  const isPending = holdingsPending || claimsPending
  const productCount = holdings?.length ?? 0
  const claimCount = claims?.length ?? 0
  const visibleProducts = (holdings ?? []).slice(0, HOME_SECTION_LIMIT)
  // Prioritise claims that need the customer's attention (an unread staff
  // message, or a pending action) so they surface within the homepage's 4-card
  // cap. Stable sort keeps the API order (recent-first) within each group.
  const needsAttention = (c: ConsumerClaimSummary) =>
    c.has_unread_notification || c.has_pending_consumer_action
  const visibleClaims = [...(claims ?? [])]
    .sort((a, b) => Number(needsAttention(b)) - Number(needsAttention(a)))
    .slice(0, HOME_SECTION_LIMIT)
  const hasClaims = claimCount > 0
  const hasProducts = productCount > 0

  return (
    <ConsumerShell>
      {/* Loading */}
      {isPending && (
        <div className="flex items-center justify-center py-20">
          <p className="text-[13px] text-[var(--ink-3)]">Loading…</p>
        </div>
      )}

      {!isPending && (
        <div className="flex flex-col gap-12">
          {/* Active Claims - only when the consumer has claims */}
          {hasClaims && (
            <section>
              <SectionHeader
                title="Active Claims"
                countLabel={`${claimCount} ${claimCount === 1 ? 'Claim' : 'Claims'}`}
                seeAllTo={
                  claimCount > HOME_SECTION_LIMIT ? () => navigate('/claims') : undefined
                }
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {visibleClaims.map((c) => (
                  <ClaimCard
                    key={c.uuid}
                    claim={c}
                    onClick={() => navigate(`/claims/${c.uuid}`)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* My Products */}
          <section>
            <SectionHeader
              title="My Products"
              countLabel={`${productCount} ${productCount === 1 ? 'Product' : 'Products'}`}
              seeAllTo={
                productCount > HOME_SECTION_LIMIT ? () => navigate('/products') : undefined
              }
              action={
                hasProducts && selfRegistrationEnabled ? (
                  <Button onClick={() => navigate('/register')} className="cursor-pointer">
                    <Plus className="h-3.5 w-3.5" />
                    Register Product
                  </Button>
                ) : undefined
              }
            />
            {hasProducts ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {visibleProducts.map((h) => (
                  <ProductCard
                    key={h.uuid}
                    holding={h}
                    onClick={() => navigate(`/products/${h.uuid}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-16 text-center">
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
                  <Package className="h-7 w-7 text-[var(--ink-3)]" />
                </div>
                <h2 className="clara-heading mb-2">No products yet</h2>
                <p className="mx-auto mb-6 max-w-xs text-[12px] text-[var(--ink-3)]">
                  When a business registers a warranty for you, it will appear here.
                  {selfRegistrationEnabled
                    ? t('products.noProductsSelfRegHint', ' You can also register products yourself.')
                    : ''}
                </p>
                {selfRegistrationEnabled && (
                  <Button onClick={() => navigate('/register')} className="cursor-pointer">
                    <Plus className="h-3.5 w-3.5" />
                    Register your first product
                  </Button>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </ConsumerShell>
  );
}
